import { useState } from 'react';
import { Card, CircleButton, MaterialSymbol, withStyles } from 'vopay';

import { useTranslation } from 'react-i18next';
import Dialog from '#shared/components/Dialog/Dialog';
import type { DialogComponentProps } from '#shared/providers/Dialog/DialogContext';
import type { EnrichmentTier } from '../useDataEnrichment';
import styles from './DataEnrichmentDialogue.module.scss';

import { Button } from '#shared/components/UI';

const cn = withStyles(styles);

type DataEnrichmentDialogueProps = DialogComponentProps<EnrichmentTier> & {
  isEnhancedAvailable: boolean;
};

type TierCardConfig = {
  tier: EnrichmentTier;
  icon: 'payments' | 'auto_awesome';
  title: string;
  features: string[];
};

export default function DataEnrichmentDialogue({
  onConfirm,
  onCancel,
  isEnhancedAvailable,
}: DataEnrichmentDialogueProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.DataEnrichment.Dialogue',
  });

  const [selectedTier, setSelectedTier] = useState<EnrichmentTier | null>(null);

  const tierCards: TierCardConfig[] = [
    {
      tier: 'basic',
      icon: 'payments',
      title: t('basicTitle', { defaultValue: 'Basic' }),
      features: [
        t('basicFeature1', { defaultValue: 'See how money flows in and out.' }),
        t('basicFeature2', { defaultValue: 'Understand income vs spending at a glance.' }),
        t('basicFeature3', { defaultValue: 'Get a snapshot of their finances.' }),
      ],
    },
    {
      tier: 'enhanced',
      icon: 'auto_awesome',
      title: t('enhancedTitle', { defaultValue: 'Enhanced' }),
      features: [
        t('enhancedFeature1', { defaultValue: 'Dive deeper into income and expenses.' }),
        t('enhancedFeature2', { defaultValue: 'Spot spending patterns and habits.' }),
        t('enhancedFeature3', { defaultValue: 'Understand financial behavior and risk level.' }),
      ],
    },
  ];

  return (
    <Card className={cn('container')}>
      <div className={cn('header')}>
        <Dialog.Title level="3">{t('title', { defaultValue: 'Data Enrichment' })}</Dialog.Title>
        <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
      </div>

      <div className={cn('tierCards')}>
        {tierCards.map(({ tier, icon, title, features }) => {
          const isLocked = tier === 'enhanced' && !isEnhancedAvailable;

          return (
            <button
              key={tier}
              type="button"
              className={cn('tierCard', tier, selectedTier === tier && 'selected', isLocked && 'locked')}
              disabled={isLocked}
              onClick={() => setSelectedTier(tier)}
              data-testid={`enrichment-tier-${tier}`}
            >
              {isLocked ? (
                <MaterialSymbol icon="lock" size="18px" color="#2D2D2D" className={cn('lockIcon')} />
              ) : (
                <span className={cn('radio', selectedTier === tier && 'radioSelected')} />
              )}

              <MaterialSymbol icon={icon} size="56px" color="#2F82FF" />
              <span className={cn('tierTitle', tier === 'enhanced' && 'enhancedTitle')}>{title}</span>

              <ul className={cn('featureList')}>
                {features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              {isLocked && (
                <span className={cn('lockedNote')}>
                  {t('enhancedLocked', { defaultValue: 'Not enabled for your account' })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <Button
        colorScheme="primary"
        className={cn('unlockButton')}
        disabled={!selectedTier}
        onClick={() => selectedTier && onConfirm(selectedTier)}
        data-testid="enrichment-unlock-confirm"
      >
        {t('enrichButton', { defaultValue: 'Enrich Data' })}
      </Button>
    </Card>
  );
}
